import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const FAQPage = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const FAQSection = ({ id, title, children }) => (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      marginBottom: '1rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e0e0e0'
    }}>
      <button
        onClick={() => toggleSection(id)}
        style={{
          width: '100%',
          padding: '1.5rem',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '1.1rem',
          fontWeight: '600',
          color: '#2e7d32'
        }}
      >
        {title}
        <span style={{
          transform: openSections[id] ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>
          ▼
        </span>
      </button>
      {openSections[id] && (
        <div style={{
          padding: '0 1.5rem 1.5rem',
          borderTop: '1px solid #f0f0f0'
        }}>
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div style={{maxWidth: '800px', margin: '0 auto', padding: '2rem'}}>
      <h1>Frequently Asked Questions</h1>
      <p style={{color: '#666', marginBottom: '2rem'}}>
        Find answers to common questions about Gene and genealogical research.
      </p>

      {/* Getting Started */}
      <FAQSection id="getting-started" title="🚀 Getting Started">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What is Gene?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene is your intelligent genealogical research assistant, specifically trained on genealogical methodologies, 
            historical records, and family documentation techniques. Unlike general AI systems, Gene understands the unique 
            challenges and methodologies of family history research.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How do I begin my first research session?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Simply log in and click "New" to start a conversation. Begin with specific questions about individuals you're 
            researching, or ask for research strategies for particular challenges you're facing.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What types of documents can I upload?</h4>
          <p style={{lineHeight: '1.6', marginBottom: '1.5rem'}}>
            Gene can analyze various genealogical documents including census records, birth/death certificates, 
            marriage records, military documents, immigration papers, and family photographs. Gene now provides enhanced 
            visual analysis for photos and automatic inline display of images.
          </p>
          
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Link 
              to="/tutorial" 
              style={{
                display: 'inline-block',
                backgroundColor: '#2e7d32',
                color: 'white',
                padding: '12px 32px',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1b5e20';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2e7d32';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              View Complete Tutorial →
            </Link>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
              Learn how to make the most of Gene's powerful features
            </p>
          </div>
        </div>
      </FAQSection>

      {/* Token Usage & Pricing - UPDATED */}
      <FAQSection id="tokens-pricing" title="💰 Token Usage & Pricing">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What are tokens?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Tokens are units that measure the complexity and length of your conversations with Gene. Each message you 
            send and receive uses tokens from your monthly allocation.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How many tokens does a typical research session use?</h4>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>Average research conversation: 4,000-6,000 tokens</li>
            <li>Document analysis: 1,000-3,000 tokens</li>
            <li>Family history chapter generation: ~15,000 tokens</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What subscription plans are available?</h4>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li><strong>Professional Plan:</strong> $100/month - 1,000,000 tokens monthly</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What happens when I reach my monthly token limit?</h4>
          <p style={{lineHeight: '1.6'}}>
            You'll receive a notification when you reach 80% of your limit. You will be alerted and asked if you 
            choose to buy more tokens until your token amount resets at the beginning of your next billing cycle. 
            The amount will be $5.00 per 100,000 of additional tokens used over your limit for the month.
          </p>
        </div>
      </FAQSection>

      {/* Research Features */}
      <FAQSection id="research-features" title="🔍 Research & AI Features">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How does Gene provide historical context?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene places your ancestors within the historical events, social conditions, and migration patterns that 
            shaped their lives, helping you understand the "why" behind their life choices and movements.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can Gene help me break through brick walls?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Yes! Gene can suggest alternative research strategies, help you identify overlooked record types, 
            recommend cluster research approaches, and guide you through indirect evidence techniques.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How does document analysis work?</h4>
          <p style={{lineHeight: '1.6'}}>
            Upload your documents and Gene will extract key genealogical information, highlight significant findings, 
            provide historical context, and suggest next research steps based on the information found.
          </p>
        </div>
      </FAQSection>

      {/* Document Management - UPDATED WITH NEW FEATURES */}
      <FAQSection id="document-management" title="📄 Document Management">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What types of documents can I upload to Gene?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene accepts a wide variety of genealogical documents to help with your family history research:
          </p>
          <p style={{marginBottom: '0.5rem', lineHeight: '1.6'}}><strong>Supported File Types:</strong></p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li><strong>PDFs</strong> - Census records, vital records, military documents, wills, obituaries</li>
            <li><strong>Images</strong> - JPG, PNG photos of documents, tombstones, family photos</li>
            <li><strong>Text Files</strong> - TXT files containing transcribed information or research notes</li>
            <li><strong>Word Documents</strong> - DOC, DOCX files with family information or research</li>
          </ul>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}><strong>Maximum File Size:</strong> 200MB per document</p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>NEW: How does automatic image display work?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene now features automatic inline image display! When Gene references any uploaded image in a response, 
            the image automatically appears within the conversation. This makes it easier to:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>View family photos while discussing them with Gene</li>
            <li>See document images alongside Gene's analysis</li>
            <li>Reference multiple images in a single conversation</li>
            <li>Click on any displayed image to view it in full size</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>NEW: Enhanced Photo Analysis</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene now provides specialized analysis for family photographs, focusing on visual elements rather than text extraction:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li><strong>Visual Dating:</strong> Analysis of clothing styles, photographic techniques, and props to estimate photo dates</li>
            <li><strong>Setting Analysis:</strong> Identification of studio vs. outdoor settings, backgrounds, and locations</li>
            <li><strong>Photography Type:</strong> Recognition of tintypes, cabinet cards, snapshots, and other formats</li>
            <li><strong>Genealogical Context:</strong> Suggestions for generational placement and family groupings</li>
            <li><strong>Preservation Tips:</strong> Recommendations for photo preservation and documentation</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How does Gene process my uploaded documents?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene uses advanced processing tailored to each document type:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li><strong>Text Documents:</strong> OCR technology extracts text from documents with genealogical information</li>
            <li><strong>Family Photos:</strong> Visual analysis identifies dating clues, settings, and photographic styles</li>
            <li><strong>Hybrid Documents:</strong> Photos with text (like photo backs) receive both visual and text analysis</li>
            <li><strong>Processing Results:</strong> Each document type receives appropriate analysis with confidence levels</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can Gene analyze the content of my documents?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Yes! Gene provides comprehensive analysis based on document type:
          </p>
          <p style={{marginBottom: '0.5rem', lineHeight: '1.6'}}><strong>For Text Documents:</strong></p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>Names, dates, places, relationships</li>
            <li>Occupations, physical descriptions</li>
            <li>Historical context and significance</li>
          </ul>
          <p style={{marginBottom: '0.5rem', lineHeight: '1.6'}}><strong>For Photographs:</strong></p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>Estimated time period based on visual elements</li>
            <li>Fashion and style indicators</li>
            <li>Photographic format and technique</li>
            <li>Possible family relationships based on groupings</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How do I use documents in my research conversations?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            When chatting with Gene, you can easily include your uploaded documents for analysis:
          </p>
          <ol style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>Click the "📄 Documents" button in the chat interface</li>
            <li>Select the documents you want Gene to analyze</li>
            <li>Ask your research question</li>
            <li>Gene will analyze the documents and display images inline when referenced</li>
          </ol>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can I download or export document analysis?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Absolutely! Gene provides multiple ways to save and export your document analysis:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li><strong>TXT</strong> - Plain text format for simple reading and sharing</li>
            <li><strong>PDF</strong> - Portable data format and can be opened in a variety of apps</li>
            <li><strong>Markdown</strong> - Formatted text with headers and organization for documentation</li>
          </ul>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Downloads include original document information, complete extracted text content, visual analysis for photos, 
            and all metadata.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How are my documents organized and stored?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Gene provides comprehensive document management to keep your research organized:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>Documents are listed by upload date (newest first)</li>
            <li>Each document shows processing status and type</li>
            <li>Photos are identified separately from text documents</li>
            <li>Search functionality across all document content</li>
            <li>Documents are securely stored and only accessible to your account</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can I search within my uploaded documents?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Yes! Gene provides powerful search capabilities across all your uploaded documents including search by 
            document name, within extracted document content (OCR text), by tags and user notes, and by document type.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What should I do if a document doesn't process correctly?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            If you encounter processing issues:
          </p>
          <ul style={{lineHeight: '1.6'}}>
            <li>For photos: Ensure good lighting and resolution for visual analysis</li>
            <li>For documents: Try re-uploading at higher resolution for better OCR quality</li>
            <li>Check that files aren't corrupted or password-protected</li>
            <li>Documents can be re-processed if initial results aren't satisfactory</li>
            <li>Consider manual transcription for very old or damaged documents</li>
          </ul>
        </div>
      </FAQSection>

      {/* NEW: Gallery Section */}
      <FAQSection id="gallery" title="🖼️ Gallery & Examples">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>What is the Gallery?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            The Gallery is a curated collection of genealogy examples, templates, and visual inspiration to help you 
            with your family history research and documentation. It showcases various ways to present and organize 
            your family history findings.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How do I access the Gallery?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Simply click on "Gallery" in the main navigation menu. The Gallery is available to all Gene users and 
            provides valuable examples for your research journey.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Create Photo Album</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            The Gallery features an interactive Photo Album creator that allows you to organize and present your 
            family photographs in a beautiful, professional format. You can:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li><strong>Build Custom Albums:</strong> Create themed photo albums for different branches of your family</li>
            <li><strong>Add Captions:</strong> Include names, dates, locations, and stories for each photo</li>
            <li><strong>Organize by Timeline:</strong> Arrange photos chronologically to show family progression</li>
            <li><strong>Group by Family:</strong> Create separate sections for different family lines</li>
            <li><strong>Include Context:</strong> Add historical notes and family stories alongside photos</li>
            <li><strong>Export Options:</strong> Download your completed albums for sharing or printing</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can I use Gallery templates for my own research?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Absolutely! All Gallery examples and templates are designed to inspire and assist your research:
          </p>
          <ul style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            <li>Use templates as starting points for your own family history documentation</li>
            <li>Adapt layouts and designs to fit your family's unique story</li>
            <li>Learn from examples of successful genealogical presentations</li>
            <li>Get ideas for organizing and sharing your research findings</li>
          </ul>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How can the Gallery help my research?</h4>
          <p style={{lineHeight: '1.6'}}>
            The Gallery serves as both inspiration and practical guidance by showing you successful approaches to 
            genealogical documentation, providing templates you can adapt, demonstrating how to present complex 
            family relationships clearly, and offering ideas for creating meaningful family history keepsakes.
          </p>
        </div>
      </FAQSection>

      {/* Privacy & Security */}
      <FAQSection id="privacy-security" title="🔒 Privacy & Security">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Is my family data secure?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Yes. Your genealogical data and family information belong to you. We use industry-standard security 
            measures to protect your information and never sell or share your personal data with third parties.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can I delete my data?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Absolutely. You can request deletion of your data at any time by contacting support@mygnology.com.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Who has access to my research?</h4>
          <p style={{lineHeight: '1.6'}}>
            Your conversations with Gene are private and secure. Only you have access to your research unless 
            you choose to share it.
          </p>
        </div>
      </FAQSection>

      {/* Account Management */}
      <FAQSection id="account-management" title="⚙️ Account Management">
        <div>
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How do I change my subscription plan?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Visit your Account page to view your current plan and token usage. Go to your "Account" page for 
            changes and billing.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>How do I reset my password?</h4>
          <p style={{marginBottom: '1rem', lineHeight: '1.6'}}>
            Go to your Account page and click "Change Password" in the Security section.
          </p>

          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Can I share my research with family members?</h4>
          <p style={{lineHeight: '1.6'}}>
            Currently, each account is individual. You can copy and share your research results through external 
            documents, Google Docs, or printed family history books.
          </p>
        </div>
      </FAQSection>

      {/* Contact Section */}
      <div style={{
        background: '#f8f9fa',
        padding: '2rem',
        borderRadius: '8px',
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Still Have Questions?</h3>
        <p style={{marginBottom: '1.5rem', lineHeight: '1.6'}}>
          Our team is here to help you succeed in your genealogical research journey.
        </p>
        <a 
          href="mailto:support@mygnology.com"
          style={{
            display: 'inline-block',
            padding: '0.75rem 2rem',
            background: '#2e7d32',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500'
          }}
        >
          Contact Support
        </a>
      </div>
    </div>
  );
};

export default FAQPage;
